import { IProcessWithRelations } from 'interfaces/process'
import { ICollection } from 'interfaces/collection'
import { LibraryItem, LibraryMeta } from 'interfaces/practice-component'

// Maps the two non-component library sources (Stack = Process, Collection) into
// the shared BuilderLibrary card view model. Keeps the `${type}:${id}` composite
// key so numeric ids don't collide with components across the merged result list.

const ICON_COUNT = 'ic_outline-account-tree'

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function tagNames(tags?: { name?: string }[]): string[] {
  return (tags ?? []).map(t => t.name ?? '').filter(Boolean)
}

/** Stack (Process) → LibraryItem. Reads the embedded `steps` relation for count.
 *  A stack carries no duration and no notes, so the child count is its only meta. */
export function mapProcessToLibraryItem(process: IProcessWithRelations): LibraryItem {
  const drillCount = process.steps?.length ?? 0

  return {
    id: process.id,
    key: `stack:${process.id}`,
    title: process.name ?? 'Untitled stack',
    type: 'stack',
    meta: [{ icon: ICON_COUNT, text: pluralize(drillCount, 'drill') }],
    tags: tagNames(process.tags),
    detail: {}
  }
}

/** Collection → LibraryItem. The card deliberately carries nothing but the stack
 *  count — no tags, no document type, no duration, no notes. A collection is not
 *  tagged at creation time, and aggregating its stacks' tags/types would balloon
 *  the card without telling the coach anything the drill-down doesn't.
 *
 *  `detail` still lists the child stacks — that feeds the inspector drill-down,
 *  not the card.
 *
 *  filter-with-relations preserves empty collections, so a membership row can
 *  carry a null/empty process — skip it. */
export function mapCollectionToLibraryItem(collection: ICollection): LibraryItem {
  const childProcesses = (collection.collectionsProcesses ?? [])
    .map(cp => cp.process)
    .filter((process): process is NonNullable<typeof process> => !!process?.id)

  const stackCount = childProcesses.length || (collection.processIds?.length ?? 0)
  const meta: LibraryMeta[] = [{ icon: ICON_COUNT, text: pluralize(stackCount, 'stack') }]

  return {
    id: collection.id,
    key: `collection:${collection.id}`,
    title: collection.name ?? 'Untitled collection',
    type: 'collection',
    meta,
    detail: {
      childLabel: 'Stacks',
      children: childProcesses.map(process => ({
        name: process.name ?? 'Untitled stack',
        meta: pluralize(process.steps?.length ?? 0, 'drill')
      }))
    }
  }
}
